import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchJson } from "@/lib/api";

// Phase 4c id-capture FR-5 / AC-10. Customer-portal IDs page. The page
// is intentionally redaction-strict: it never renders the ID image
// (the blob is admin/dispatch-only, FR-3) and never the legal_name —
// only the kind, last4, expiry, and status.

interface IdentificationSummary {
  id: number;
  id_kind: string;
  id_kind_label: string;
  issuing_jurisdiction: string | null;
  last4: string | null;
  expires_on: string | null;
  status: "pending_review" | "accepted" | "rejected" | "expired";
  rejected_reason: string | null;
}

interface AuthorizedIndividualSummary {
  id: number;
  name: string;
  relationship: string;
  phone_e164: string | null;
  email: string | null;
  accepted_ids_count: number;
}

interface IdsListPayload {
  identifications: IdentificationSummary[];
  authorized_individuals: AuthorizedIndividualSummary[];
}

const KIND_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "drivers_license", label: "Driver's license" },
  { value: "state_id", label: "State ID" },
  { value: "passport", label: "Passport" },
  { value: "military_id", label: "Military ID" },
  { value: "tribal_id", label: "Tribal ID" },
  { value: "other_government", label: "Other government ID" }
];

export function IdsPage(): JSX.Element {
  const list = useQuery({
    queryKey: ["portal", "ids"],
    queryFn: () => fetchJson<IdsListPayload>("/portal/account/identifications.json")
  });

  return (
    <section className="flex flex-col gap-4">
      <header className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold text-brand-black">My IDs</h1>
        <p className="text-xs text-zinc-500">
          Government-issued IDs we keep on file so we can release equipment.
        </p>
      </header>

      {list.isPending ? <p className="text-sm text-zinc-500">Loading…</p> : null}
      {list.isError ? (
        <p className="text-sm text-brand-red">Could not load your IDs. Try again.</p>
      ) : null}

      {list.data ? (
        <>
          <IdList rows={list.data.identifications} />
          <UploadCard />
          <AuthorizedIndividualsList rows={list.data.authorized_individuals} />
        </>
      ) : null}
    </section>
  );
}

function IdList({ rows }: { rows: IdentificationSummary[] }): JSX.Element {
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-zinc-300 p-4 text-sm text-zinc-600">
        You haven't added any IDs yet. Use the form below to upload one.
      </div>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {rows.map((row) => (
        <li key={row.id} className="rounded-md border border-zinc-200 p-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-brand-black">
                {row.id_kind_label}
                {row.issuing_jurisdiction ? ` · ${row.issuing_jurisdiction}` : ""}
              </p>
              <p className="text-xs text-zinc-500">
                {row.last4 ? `········${row.last4}` : "no last-4 on file"}
                {row.expires_on ? ` · expires ${row.expires_on}` : ""}
              </p>
              {row.rejected_reason ? (
                <p className="text-xs text-brand-red">Reason: {row.rejected_reason}</p>
              ) : null}
            </div>
            <StatusBadge status={row.status} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function StatusBadge({ status }: { status: IdentificationSummary["status"] }): JSX.Element {
  const map: Record<IdentificationSummary["status"], { label: string; cls: string }> = {
    pending_review: { label: "Pending review", cls: "bg-amber-100 text-amber-800" },
    accepted: { label: "Accepted", cls: "bg-emerald-100 text-emerald-800" },
    rejected: { label: "Re-upload needed", cls: "bg-red-100 text-red-800" },
    expired: { label: "Expired", cls: "bg-zinc-200 text-zinc-700" }
  };
  const { label, cls } = map[status];
  return <span className={`rounded-full px-3 py-1 text-xs ${cls}`}>{label}</span>;
}

function UploadCard(): JSX.Element {
  const qc = useQueryClient();
  const [kind, setKind] = useState("drivers_license");
  const [file, setFile] = useState<File | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Please pick an image.");
      const form = new FormData();
      form.append("id_kind", kind);
      form.append("image_front", file);
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";
      const res = await fetch("/portal/account/identifications.json", {
        method: "POST",
        headers: { "X-CSRF-Token": csrf, Accept: "application/json" },
        body: form,
        credentials: "same-origin"
      });
      if (!res.ok) throw new Error((await res.json()).message ?? "Upload failed");
      return res.json();
    },
    onSuccess: () => {
      setFile(null);
      qc.invalidateQueries({ queryKey: ["portal", "ids"] });
    }
  });

  return (
    <form
      className="rounded-md border border-zinc-200 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <p className="text-sm font-medium text-brand-black">Add a new ID</p>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
        <label className="text-xs text-zinc-600">
          Kind
          <select
            className="ml-2 rounded border border-zinc-300 px-2 py-1 text-sm"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            {KIND_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="text-sm"
        />
        <button
          type="submit"
          disabled={!file || mutation.isPending}
          className="rounded bg-brand-red px-3 py-1 text-sm text-brand-white disabled:opacity-60"
        >
          {mutation.isPending ? "Uploading…" : "Upload"}
        </button>
      </div>
      {mutation.isError ? (
        <p className="mt-2 text-xs text-brand-red">{(mutation.error as Error).message}</p>
      ) : null}
    </form>
  );
}

function AuthorizedIndividualsList({
  rows
}: {
  rows: AuthorizedIndividualSummary[];
}): JSX.Element {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-sm font-medium text-brand-black">Authorized individuals</h2>
      <ul className="flex flex-col gap-2">
        {rows.map((ai) => (
          <li
            key={ai.id}
            className="rounded-md border border-zinc-200 p-3 text-sm text-brand-black"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{ai.name}</p>
                <p className="text-xs text-zinc-500">
                  {ai.relationship} ·{" "}
                  {ai.accepted_ids_count > 0
                    ? `${ai.accepted_ids_count} ID on file`
                    : "no ID yet"}
                </p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
